import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface MediaRecorderProps {
  onRecordingComplete: (url: string, type: 'audio' | 'video') => void;
  mediaType: 'audio' | 'video';
}

export const MediaRecorder = ({ onRecordingComplete, mediaType }: MediaRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const mediaRecorderRef = useRef<globalThis.MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
  };

  // For video: try to open camera preview when dialog mounts
  useEffect(() => {
    if (mediaType !== 'video') return;
    let cancelled = false;

    const openPreview = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } },
          audio: true,
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
          await videoPreviewRef.current.play();
        }
      } catch (err: any) {
        console.warn("Camera preview not available yet:", err.message);
      }
    };

    openPreview();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [mediaType]);

  // Clean up on unmount
  useEffect(() => {
    return () => stopStream();
  }, []);

  const startRecording = async () => {
    try {
      let stream = streamRef.current;

      // If no stream yet (audio, or video preview failed), request one now
      if (!stream) {
        const constraints = mediaType === 'audio'
          ? { audio: true }
          : { video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 24 } }, audio: true };
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        if (mediaType === 'video' && videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream;
          await videoPreviewRef.current.play();
        }
      }

      const mimeType = mediaType === 'audio' ? 'audio/webm' : 'video/webm';
      const recorder = new window.MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        console.log('[MediaRecorder] ondataavailable, size:', event.data.size);
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event: any) => {
        console.error('[MediaRecorder] recorder error:', event.error);
        toast.error('Recording error occurred');
      };

      recorder.onstop = async () => {
        console.log('[MediaRecorder] onstop fired, chunks:', chunksRef.current.length);
        try {
          setIsUploading(true);

          if (chunksRef.current.length === 0) {
            toast.error("No recording data was captured. Please try again.");
            setUploadStatus("");
            return;
          }

          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            toast.error("Please log in to save recordings");
            setUploadStatus("");
            return;
          }

          const blob = new Blob(chunksRef.current, { type: mimeType });
          const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);
          console.log('[MediaRecorder] blob size:', blob.size, `(${sizeMB} MB)`);
          setUploadStatus(`Uploading ${sizeMB} MB...`);

          const filename = `${user.id}/${Date.now()}.webm`;

          const { error: uploadError } = await supabase.storage
            .from('journal_media')
            .upload(filename, blob);

          if (uploadError) {
            console.error('[MediaRecorder] upload error:', uploadError);
            throw uploadError;
          }

          const { data: { publicUrl } } = supabase.storage
            .from('journal_media')
            .getPublicUrl(filename);

          console.log('[MediaRecorder] upload success, url:', publicUrl);
          setUploadStatus("");
          onRecordingComplete(publicUrl, mediaType);
          toast.success(`${mediaType} recording saved`);
        } catch (error) {
          console.error('[MediaRecorder] upload error:', error);
          setUploadStatus("");
          toast.error(`Failed to save ${mediaType} recording`);
        } finally {
          setIsUploading(false);
          stopStream();
        }
      };

      recorder.start(1000);
      setIsRecording(true);
      toast.success(`${mediaType} recording started`);
    } catch (error: any) {
      console.error('Recording error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error(`${mediaType === 'audio' ? 'Microphone' : 'Camera'} access denied. Please allow it in your browser settings.`);
      } else if (error.name === 'NotFoundError') {
        toast.error(`${mediaType === 'audio' ? 'Microphone' : 'Camera'} not found. Please check your device.`);
      } else {
        toast.error(`Failed to start recording: ${error.message}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div className="space-y-4">
      {mediaType === 'video' && (
        <div className="relative aspect-video w-full bg-black rounded-lg overflow-hidden">
          <video
            ref={videoPreviewRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="flex justify-center gap-2">
        {!isRecording ? (
          <Button onClick={startRecording} disabled={isUploading}>
            Start {mediaType === 'audio' ? 'Audio' : 'Video'} Recording
          </Button>
        ) : (
          <Button onClick={stopRecording} variant="destructive" disabled={isUploading}>
            Stop Recording
          </Button>
        )}
      </div>

      {isRecording && (
        <p className="text-sm text-center text-[#3BAFDA]">Recording in progress...</p>
      )}

      {isUploading && (
        <div className="flex items-center justify-center gap-2 rounded-md border border-[#CCCCCC] bg-[#E8F0F8] px-3 py-2 text-sm text-[#333B44]">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>{uploadStatus || "Uploading recording..."}</span>
        </div>
      )}
    </div>
  );
};
